<template>
  <div class="Tooltip">
    <div
      class="Tooltip__inner"
      :style="computedStyle"
      @mouseover="mouseOver"
      @mouseleave="mouseLeave"
    >
      <slot />
      <span
        v-if="hover"
        class="Tooltip__balloon"
        :class="directionClass"
        :style="width"
        >{{ text }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

defineOptions({
  name: 'TooltipBase',
});

interface Props {
  text?: string;
  size?: number | string;
  direction?: string;
  left?: string | number;
  afterLeft?: string | number;
  top?: string | number;
  afterTop?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  size: '',
  direction: 'up',
  left: '',
  afterLeft: '',
  top: '',
  afterTop: '',
});

const hover = ref(false);

const width = computed(() => {
  return {
    '--width': `${props.size}px`,
  };
});

const directionClass = computed(() => {
  return 'Tooltip__balloon--' + props.direction;
});

const computedStyle = computed(() => {
  return {
    '--tooltip-left': props.left ? props.left : '50%',
    '--tooltip-after-left': props.afterLeft ? props.afterLeft : '50%',
    '--tooltip-left-transform': props.left ? 'none' : 'translate(-50%, 0)',
    '--tooltip-top': props.top ? props.top : '50%',
    '--tooltip-after-top': props.afterTop ? props.afterTop : '50%',
    '--tooltip-top-transform': props.top ? 'none' : 'translate(0, -50%)',
  };
});

function mouseOver() {
  if (props.text === '') {
    return false;
  }
  hover.value = true;
}

function mouseLeave(): void {
  hover.value = false;
}
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_forward.scss' as *;

.Tooltip {
  &__inner {
    position: relative;
    display: inline-block;
  }

  &__balloon {
    position: absolute;
    z-index: $service-z-index-ff;
    // bottom: calc(100% + $service-unit);
    // left: 50%;
    display: inline-block;
    width: var(--width);
    padding: $service-unit $service-unit * 2;
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mp;
    color: $color-white-100;
    white-space: nowrap;
    background-color: $color-black-100;
    border-radius: $service-unit;
    box-shadow: $service-shadow-p;
    //transform: translateX(-50%);

    &::after {
      position: absolute;
      // top: 100%;
      // left: 50%;
      content: '';
      border: 5px solid transparent;
      // border-top: 5px solid $color-black-100;
      // transform: translateX(-50%);
    }

    &--up {
      bottom: calc(100% + $service-unit);
      left: var(--tooltip-left);
      transform: var(--tooltip-left-transform);

      &::after {
        top: 100%;
        left: var(--tooltip-after-left);
        border-top: 5px solid $color-black-100;
        transform: var(--tooltip-left-transform);
      }
    }

    &--down {
      top: calc(100% + $service-unit);
      left: var(--tooltip-left);
      transform: var(--tooltip-left-transform);

      &::after {
        bottom: 100%;
        left: var(--tooltip-after-left);
        border-bottom: 5px solid $color-black-100;
        transform: var(--tooltip-left-transform);
      }
    }

    &--right {
      top: var(--tooltip-top);
      left: calc(100% + $service-unit);
      transform: var(--tooltip-top-transform);

      &::after {
        top: var(--tooltip-after-top);
        left: -10px;
        border-right: 5px solid $color-black-100;
        transform: var(--tooltip-top-transform);
      }
    }

    &--left {
      top: var(--tooltip-top);
      right: calc(100% + $service-unit);
      transform: var(--tooltip-top-transform);

      &::after {
        top: var(--tooltip-after-top);
        right: -10px;
        border-left: 5px solid $color-black-100;
        transform: var(--tooltip-top-transform);
      }
    }
  }
  @media (max-width: 1024px) {
    &__balloon {
      display: none;
    }
  }
}
</style>
